// ============================================================
// Formatters — shared by web (frontend/) and mobile (mobile/).
// Ported verbatim from frontend/src/utils/format.js (behaviour preserved).
// ============================================================

import { isLevelUnit } from "./rateIndexUnits";

export type Formattable = string | number | Date | null | undefined;

// ── The display time zone ─────────────────────────────────────────────────
// Times are STORED in UTC and DISPLAYED in the signed-in person's saved zone
// (dbo.Users.TimeZone, returned by /api/user-profile). Each client calls
// setDisplayTimeZone() once the profile loads; until then — or for someone who
// has never saved one — the device's own zone stands in. Every formatter below
// reads it, so a time renders identically on a laptop in Chicago and on a phone
// that happens to be in New York.

let savedZone: string | null = null;

function validZone(tz: string | null | undefined): string | null {
  if (!tz) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(0);
    return tz;
  } catch {
    return null;
  }
}

function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Set the zone every formatter renders in (null/unknown → the device zone). */
export function setDisplayTimeZone(tz: string | null | undefined): void {
  savedZone = validZone(tz);
}

/** The IANA zone times are displayed in right now. */
export function displayTimeZone(): string {
  return savedZone || deviceZone();
}

// "2026-09-22" or an exact UTC midnight: a calendar day, not an instant.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/;

function toInstant(v: Formattable): Date | null {
  if (v == null || v === "") return null;
  const d = v instanceof Date ? v : new Date(typeof v === "number" ? v : String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Y/M/D of an instant in the display zone. */
function zonedYmd(d: Date): [number, number, number] {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: displayTimeZone(), year: "numeric", month: "numeric", day: "numeric",
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return [get("year"), get("month"), get("day")];
}

/**
 * The calendar day a value names, as a LOCAL-midnight Date (so local getters
 * and toLocaleDateString without a zone read it back unchanged). A date-only
 * value keeps its own day; an instant takes the day it falls on in the display
 * zone. Null when unreadable.
 */
export function toLocalCalendarDate(v: Formattable): Date | null {
  if (v == null || v === "") return null;
  if (typeof v === "string") {
    const m = DATE_ONLY_RE.exec(v.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = toInstant(v);
  if (!d) return null;
  // A Date at exact UTC midnight is how the SQL driver hands back a DATE
  // column — treat it as that day rather than as 6/7 PM the evening before.
  if (
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0
  ) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  const [y, m, day] = zonedYmd(d);
  return new Date(y, m - 1, day);
}

/**
 * Format an INSTANT (a UTC timestamp) in the display zone — the one call every
 * screen should use in place of `new Date(x).toLocaleString(...)`. Empty string
 * for a missing or unreadable value. Never use it for a DATE column; that is
 * fmt.date's job (a calendar day has no zone to convert).
 */
export function formatInstant(v: Formattable, opts: Intl.DateTimeFormatOptions = {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
}): string {
  const d = toInstant(v);
  if (!d) return "";
  return d.toLocaleString("en-US", { ...opts, timeZone: displayTimeZone() });
}

/** True when an instant falls on today, in the display zone. */
export function isTodayInZone(v: Formattable): boolean {
  const d = toInstant(v);
  if (!d) return false;
  const a = zonedYmd(d), b = zonedYmd(new Date());
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/** "YYYY-MM-DD" of an instant in the display zone ("" when unreadable). */
export function isoDateInZone(v: Formattable): string {
  const d = toInstant(v);
  if (!d) return "";
  const [y, m, day] = zonedYmd(d);
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Today's date in the display zone as "YYYY-MM-DD" — the default for a date input. */
export function todayIsoInZone(): string {
  const [y, m, d] = zonedYmd(new Date());
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Today's calendar date in the display zone, as a local-midnight Date. */
export function todayInZone(): Date {
  const [y, m, d] = zonedYmd(new Date());
  return new Date(y, m - 1, d);
}

export const fmt = {
  currency: (v: Formattable): string =>
    v != null
      ? `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : "—",

  pct: (v: Formattable): string =>
    v != null ? `${(Number(v) * 100).toFixed(2)}%` : "—",

  // A rate-index value rendered in ITS OWN unit. A percent series is a fraction
  // and reads as a percentage; an index level (the S&P 500) is points and reads
  // as a plain thousands-separated number. Passing a level through fmt.pct is
  // how 6,450.16 becomes "645016.00%", so every rate-index surface goes through
  // this instead. An unknown unit falls back to percent — see rateIndexUnits.ts.
  indexValue: (v: Formattable, unit?: string | null): string => {
    if (v == null) return "—";
    if (!isLevelUnit(unit)) return fmt.pct(v);
    return Number(v).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },

  // A calendar date in the VIEWER'S time zone. Two kinds of value arrive here
  // and they need opposite handling:
  //  - a DATE column ("2026-09-22", or the driver's "2026-09-22T00:00:00.000Z")
  //    is a calendar day with no zone — it must render as that day everywhere,
  //    so it is read off its digits, never through the local clock (at UTC
  //    midnight the local clock in the Americas says the day before);
  //  - a timestamp ("...UTC" columns, "2026-09-22T02:30:00Z") is an instant,
  //    and the day it falls on depends on where you are. Slicing its first ten
  //    characters showed the UTC day — a 9 PM Central upload read as tomorrow.
  date: (v: Formattable): string => {
    if (!v) return "—";
    const d = toLocalCalendarDate(v);
    if (!d) return "—";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  },

  // Date + time for a timestamp (e.g. chat message stamps), in the display
  // zone: time-only when the stamp is today ("3:42 PM"), otherwise date + time
  // ("Aug 6, 3:42 PM"). Empty string for a missing/bad value.
  dateTime: (v: Formattable): string => {
    if (!toInstant(v)) return "";
    const time = formatInstant(v, { hour: "numeric", minute: "2-digit" });
    return isTodayInZone(v)
      ? time
      : `${formatInstant(v, { month: "short", day: "numeric" })}, ${time}`;
  },

  // Full stamp: "Aug 6, 2026, 3:42 PM".
  stamp: (v: Formattable): string => formatInstant(v),

  // Clock time only: "3:42 PM".
  time: (v: Formattable): string => formatInstant(v, { hour: "numeric", minute: "2-digit" }),

  initials: (name?: string | null): string =>
    name
      ? name
          .split(" ")
          .map((w) => w[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "?",

  fileSize: (bytes: number): string =>
    bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`,
};
